import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import Spinner from "../components/Spinner";
import PremiumUpsell from "../components/PremiumUpsell";
import SpendHistorySection from "../components/SpendHistorySection";
import BudgetTrackerSection from "../components/BudgetTrackerSection";
import {usePremiumGate} from "../hooks/usePremiumGate";
import {pluralItemsOf} from '../utils/plural';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface ValuedItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    priceRub: number | null;
    totalWears: number;
    costPerWear: number | null;
}

interface WardrobeValue {
    totalItems: number;
    itemsWithPrice: number;
    itemsWithoutPrice: number;
    totalInvested: number | null;
    averageCostPerWear: number | null;
    topExpensive: ValuedItem[];
}

const money = (n: number | null | undefined): string =>
    n == null ? '—' : `${Math.round(n).toLocaleString('ru-RU')} ₽`;

const WardrobeValuePage: React.FC = () => {
    const {selectedOwnerId, selectedOwnerDisplayName, isOwnWardrobe} = useWardrobe();
    const [data, setData] = useState<WardrobeValue | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const {premiumGate, capturePremiumGate, resetPremiumGate} = usePremiumGate();

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        resetPremiumGate();
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<WardrobeValue>('/wardrobe-value', {params});
            setData(res.data);
        } catch (e) {
            // Стоимость гардероба — premium-аналитика: на Free страница упирается в гейт.
            // Это не сбой загрузки, «Повторить» тут бессмысленно (R17) — объясняем и
            // показываем путь апгрейда прямо на месте данных.
            if (!capturePremiumGate(e, 'Стоимость гардероба доступна в Premium')) {
                setError(true);
            }
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId, capturePremiumGate, resetPremiumGate]);

    useDocumentTitle();

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return <Spinner/>;
    }

    if (premiumGate) {
        return (
            <div className="p-4 md:p-6 max-w-3xl">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Стоимость гардероба</h1>
                <PremiumUpsell
                    testId="wardrobe-value-premium"
                    title={premiumGate.title}
                    description={premiumGate.description}
                />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить стоимость гардероба</p>
                <button onClick={load} className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    Повторить
                </button>
            </div>
        );
    }

    const v = data;
    const title = isOwnWardrobe
        ? 'Стоимость гардероба'
        : `Стоимость: ${selectedOwnerDisplayName || selectedOwnerId?.slice(0, 8) + '...'}`;

    const stat = (label: string, value: string, testid: string) => (
        <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-4" data-testid={testid}>
            <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">{value}</div>
        </div>
    );

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    Сколько вложено в гардероб и насколько выгодно вы его носите. Цены берутся из карточек вещей.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="value-stats">
                {stat('Вложено', money(v.totalInvested), 'stat-invested')}
                {stat('Средняя стоимость носки', money(v.averageCostPerWear), 'stat-avg-cpw')}
                {stat('Вещей', String(v.totalItems), 'stat-total')}
                {stat('С ценой / без', `${v.itemsWithPrice} / ${v.itemsWithoutPrice}`, 'stat-priced')}
            </div>

            {v.itemsWithoutPrice > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="value-hint">
                    У {pluralItemsOf(v.itemsWithoutPrice)} не указана цена — добавьте её, чтобы расчёт был точнее.
                </p>
            )}

            <BudgetTrackerSection ownerId={selectedOwnerId}/>

            <SpendHistorySection ownerId={selectedOwnerId}/>

            {v.topExpensive.length > 0 && (
                <section data-testid="value-top">
                    <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Самые дорогие</h2>
                    <ul className="space-y-2">
                        {v.topExpensive.map(it => (
                            <li key={it.id}
                                className="flex justify-between items-center rounded-xl border border-gray-200 dark:border-ink-700 p-3">
                                <span className="min-w-0 text-gray-900 dark:text-gray-100 truncate">
                                    {it.name}
                                    {it.categoryName && <span className="text-gray-500"> · {it.categoryName}</span>}
                                </span>
                                <span className="text-right shrink-0 ml-3">
                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{money(it.priceRub)}</span>
                                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                                        {it.costPerWear != null ? `${money(it.costPerWear)} / носка` : 'не носили'}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

export default WardrobeValuePage;
